// pipe: driver UI polish — the shared screen frame (SSOT for the driver app's
// padded, safe-area-aware screen body + an optional header with a back button and
// title). Both the list and the pickup-execution views (and their loading/empty/
// error states) render inside it, so the frame is declared once (DRY).
// Presentational only — no state, no service, no router — so the pure Vitest seams
// (pickups-view / pickup-exec-view) keep rendering it without pulling DI; the back
// action is a plain callback the container supplies.
// Subpath import (the package exports map supports it) so the lean driver
// bundle pulls only the button module, never the whole @affine/component barrel.
import { IconButton } from '@affine/component/ui/button';
import { useI18n } from '@affine/i18n';
import { ArrowLeftSmallIcon } from '@blocksuite/icons/rc';
import type { ReactNode } from 'react';

import * as ui from './driver-ui.css';

export interface DriverScreenProps {
  /** The screen heading. Omitted on the bare error/loading panels. */
  title?: ReactNode;
  /** When provided, a back affordance is shown in the header (the standard
   *  Android navigation pattern). The container owns where "back" goes. */
  onBack?: () => void;
  children: ReactNode;
}

export function DriverScreen({ title, onBack, children }: DriverScreenProps) {
  const t = useI18n();
  const hasHeader = onBack != null || title != null;
  return (
    <div className={ui.screenBody}>
      {hasHeader ? (
        <header className={ui.screenHeader}>
          {onBack != null ? (
            <IconButton
              size="24"
              onClick={onBack}
              aria-label={t['com.affine.pipe.driver.back']()}
            >
              <ArrowLeftSmallIcon />
            </IconButton>
          ) : null}
          {title != null ? <h1 className={ui.screenTitle}>{title}</h1> : null}
        </header>
      ) : null}
      {children}
    </div>
  );
}
